import { api } from './api.js';

export type PushStatus =
  | 'unsupported'   // navigateur sans service worker / Push API
  | 'denied'        // permission refusee : seul l'utilisateur peut revenir dessus
  | 'disabled'      // serveur sans cles VAPID
  | 'stalled'       // service worker jamais active sur cet appareil
  | 'off'           // supporte, mais pas d'abonnement actif
  | 'on';

/**
 * La cle VAPID publique voyage en base64url ; l'API Push attend les octets
 * bruts de la cle P-256 non compressee.
 */
function decodeVapidKey(base64url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  // Vue construite sur un ArrayBuffer explicite : `Uint8Array.from` produit
  // un Uint8Array<ArrayBufferLike>, que BufferSource n'accepte pas.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export const pushSupported = (): boolean =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/**
 * Deux patiences, parce que les deux attentes n'ont pas le meme cout.
 *
 * Le releve d'etat tourne a l'ouverture de l'ecran : il doit rendre la main
 * vite, quitte a se tromper, puisque se tromper ne coute qu'un bouton
 * « reinstaller » propose a tort. L'abonnement, lui, part d'un clic : l'utilisateur
 * attend volontairement, et abandonner trop tot lui rend un echec alors qu'une
 * premiere installation sur reseau mobile lent peut depasser cinq secondes.
 */
const SW_READY_TIMEOUT_MS = 5_000;
const SW_READY_PATIENT_MS = 20_000;

/**
 * `navigator.serviceWorker.ready` n'expire jamais. Tant qu'aucun worker
 * n'atteint l'etat *activated* pour cette portee — enregistrement refuse,
 * installation interrompue — elle attend sans fin, et tout ce qui l'attend
 * avec elle : l'ecran des reglages affichait « Verification des
 * notifications… » pour toujours, sans un bouton pour s'en sortir.
 *
 * On lui met donc une borne, et l'absence de worker devient un etat comme un
 * autre, que l'interface sait nommer et reparer.
 */
async function activeRegistration(
  timeoutMs = SW_READY_TIMEOUT_MS,
): Promise<ServiceWorkerRegistration | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

function sameKey(a: ArrayBuffer | null, b: Uint8Array): boolean {
  if (!a || a.byteLength !== b.length) return false;
  const bytes = new Uint8Array(a);
  return bytes.every((value, index) => value === b[index]);
}

/**
 * Etat de cet appareil, *resynchronise avec le serveur* au passage.
 *
 * L'abonnement du navigateur ne suffit pas a dire « on » : le serveur a pu le
 * perdre ou le mettre de cote (voir `sendToUser`), et l'appareil se croirait
 * abonne sans plus jamais rien recevoir. Le renvoyer est idempotent — une
 * mise a jour sur l'endpoint — et le remet en service.
 */
export async function getPushStatus(): Promise<PushStatus> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';

  const { enabled, publicKey } = await api.get<{ enabled: boolean; publicKey: string | null }>(
    '/push/public-key',
  );
  if (!enabled || !publicKey) return 'disabled';

  const reg = await activeRegistration();
  if (!reg) return 'stalled';

  const subscription = await reg.pushManager.getSubscription();
  if (!subscription) return 'off';
  // Cree avec une autre paire VAPID, il ne sera jamais servi : il faut
  // repasser par `enablePush`, qui le remplace.
  if (!sameKey(subscription.options.applicationServerKey, decodeVapidKey(publicKey))) return 'off';
  if (Notification.permission !== 'granted') return 'off';

  await api.post('/push/subscribe', subscription.toJSON());
  return 'on';
}

export async function enablePush(): Promise<PushStatus> {
  if (!pushSupported()) return 'unsupported';

  const { enabled, publicKey } = await api.get<{ enabled: boolean; publicKey: string | null }>(
    '/push/public-key',
  );
  if (!enabled || !publicKey) return 'disabled';

  // Chrome exige que la demande parte d'un geste utilisateur : cette fonction
  // doit donc etre appelee depuis un handler de clic, pas au chargement.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  const reg = await activeRegistration(SW_READY_PATIENT_MS);
  if (!reg) return 'stalled';

  const existing = await reg.pushManager.getSubscription();
  // Un abonnement issu d'une ancienne paire VAPID ne pourra plus etre
  // dechiffre : on le remplace au lieu de le reutiliser.
  if (existing) await existing.unsubscribe().catch(() => undefined);

  const subscription = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: decodeVapidKey(publicKey),
  });

  await api.post('/push/subscribe', subscription.toJSON());
  return 'on';
}

export async function disablePush(): Promise<PushStatus> {
  if (!pushSupported()) return 'unsupported';

  const reg = await activeRegistration();
  const subscription = await reg?.pushManager.getSubscription();
  if (subscription) {
    await api.post('/push/unsubscribe', { endpoint: subscription.endpoint }).catch(() => undefined);
    await subscription.unsubscribe().catch(() => undefined);
  }
  return 'off';
}

export const sendTestPush = () =>
  api.post<{
    sent: number;
    removed: number;
    failed: number;
    errors: Array<{ status: number | null; message: string }>;
  }>('/push/test');
